import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiException } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Spinner } from '../../components/ui/spinner';
import { Modal } from '../../components/ui/modal';
import { fmtBRL, fmtCompetencia } from '../../lib/format';
import { AlertTriangle, RotateCcw } from 'lucide-react';

const statusLabel: Record<string, string> = { lancada: 'Lançada', aprovada: 'Aprovada', rejeitada: 'Rejeitada', estornada: 'Estornada' };

interface Indevido {
  pagamentoId: string; parcelaId: string | null; contratoId: string;
  numeroContrato: string; nome: string; documentoMask: string;
  valor: number; compPagamento: string; ultimaCompContrato: string;
}
interface Devolucao {
  id: string; numeroContrato: string | null; nome: string | null;
  documentoMask: string | null; tipo: string; valor: number;
  competencia: string; motivo: string; status: string;
}

export function DevolucoesPage() {
  const qc = useQueryClient();
  const [aba, setAba] = useState<'indevidos' | 'lancadas'>('indevidos');
  const [ressarcir, setRessarcir] = useState<Indevido | null>(null);
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<string | null>(null);

  const indevidos = useQuery({
    queryKey: ['dev-indevidos'],
    queryFn: () => api<{ items: Indevido[]; total: number }>('/devolucoes/indevidos'),
    enabled: aba === 'indevidos',
  });
  const lancadas = useQuery({
    queryKey: ['dev-lancadas'],
    queryFn: () => api<{ items: Devolucao[]; total: number }>('/devolucoes'),
    enabled: aba === 'lancadas',
  });

  const criar = useMutation({
    mutationFn: (i: Indevido) => api('/devolucoes', {
      method: 'POST',
      body: { pagamentoId: i.pagamentoId, tipo: 'desconto_indevido', motivo: motivo || `Desconto indevido em ${i.compPagamento} (contrato encerrado em ${i.ultimaCompContrato})` },
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dev-indevidos'] });
      qc.invalidateQueries({ queryKey: ['dev-lancadas'] });
      setRessarcir(null); setMotivo('');
    },
    onError: (e) => setErro(e instanceof ApiException ? e.message : 'Erro ao ressarcir'),
  });

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Devolução de Valores</h1>
        <p className="text-sm text-muted-foreground">Descontos indevidos a ressarcir e devoluções lançadas</p>
      </div>

      <div className="mb-4 flex gap-2 border-b">
        <button className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${aba === 'indevidos' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          onClick={() => setAba('indevidos')}>A ressarcir</button>
        <button className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${aba === 'lancadas' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          onClick={() => setAba('lancadas')}>Devoluções lançadas</button>
      </div>

      {aba === 'indevidos' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              Descontos indevidos
              {indevidos.data && <span className="text-sm font-normal text-muted-foreground">{indevidos.data.total} caso(s)</span>}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {indevidos.isLoading ? <div className="flex justify-center p-8"><Spinner /></div> : (
              <table className="w-full text-sm">
                <thead><tr className="border-b text-left text-gray-500">
                  <th className="py-2">Associado</th><th className="py-2">Contrato</th>
                  <th className="py-2">Descontou</th><th className="py-2">Encerrou</th>
                  <th className="py-2 text-right">Valor</th><th className="py-2 w-20"></th>
                </tr></thead>
                <tbody>
                  {indevidos.data?.items.map((i) => (
                    <tr key={i.pagamentoId} className="border-b">
                      <td className="py-2">{i.nome}<br /><span className="text-xs text-gray-500">{i.documentoMask}</span></td>
                      <td className="py-2 text-xs">{i.numeroContrato}</td>
                      <td className="py-2 text-red-700">{fmtCompetencia(i.compPagamento)}</td>
                      <td className="py-2">{fmtCompetencia(i.ultimaCompContrato)}</td>
                      <td className="py-2 text-right font-semibold">{fmtBRL(i.valor)}</td>
                      <td className="py-2 text-right">
                        <Button size="sm" variant="outline" onClick={() => { setRessarcir(i); setErro(null); }}>
                          <RotateCcw className="h-3 w-3 mr-1" /> Ressarcir
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {indevidos.data?.items.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-gray-400">Nenhum desconto indevido detectado.</td></tr>}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      )}

      {aba === 'lancadas' && (
        <Card>
          <CardHeader><CardTitle>Devoluções lançadas {lancadas.data && <span className="text-sm font-normal text-muted-foreground">{lancadas.data.total}</span>}</CardTitle></CardHeader>
          <CardContent>
            {lancadas.isLoading ? <div className="flex justify-center p-8"><Spinner /></div> : (
              <table className="w-full text-sm">
                <thead><tr className="border-b text-left text-gray-500">
                  <th className="py-2">Associado</th><th className="py-2">Contrato</th>
                  <th className="py-2">Competência</th><th className="py-2 text-right pr-6">Valor</th><th className="py-2">Status</th>
                </tr></thead>
                <tbody>
                  {lancadas.data?.items.map((d) => (
                    <tr key={d.id} className="border-b">
                      <td className="py-2">{d.nome}</td>
                      <td className="py-2 text-xs">{d.numeroContrato}</td>
                      <td className="py-2">{fmtCompetencia(d.competencia)}</td>
                      <td className="py-2 text-right pr-6">{fmtBRL(d.valor)}</td>
                      <td className="py-2">{statusLabel[d.status] ?? d.status}</td>
                    </tr>
                  ))}
                  {lancadas.data?.items.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-gray-400">Nenhuma devolução lançada.</td></tr>}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      )}

      {ressarcir && (
        <Modal open onClose={() => setRessarcir(null)} title="Ressarcir desconto indevido">
          <div className="space-y-3">
            {erro && <div className="rounded bg-red-50 border border-red-200 p-2 text-sm text-red-800">{erro}</div>}
            <p className="text-sm"><strong>{ressarcir.nome}</strong> — {fmtBRL(ressarcir.valor)} descontado em {fmtCompetencia(ressarcir.compPagamento)}, mas o contrato encerrou em {fmtCompetencia(ressarcir.ultimaCompContrato)}.</p>
            <textarea className="w-full rounded border p-2 text-sm" rows={3} placeholder="Motivo (opcional)" value={motivo} onChange={(e) => setMotivo(e.target.value)} />
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" onClick={() => setRessarcir(null)}>Cancelar</Button>
              <Button disabled={criar.isPending} onClick={() => criar.mutate(ressarcir)}>Confirmar ressarcimento</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
